import React, { FC, useCallback, useContext, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { NavLink, useHistory } from 'react-router-dom'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'

import { classValidatorResolver } from '@hookform/resolvers/class-validator'
import { FormControl, Grid, InputLabel, TextField, Typography } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { withStyles } from '@mui/styles'

import { CrmLoginRequestDto } from '@sparling/financial-common'

import useStyles from './useStyle'
import { rxios } from '../../utils/NetUtils'
import { routes } from '../../constants/routes'
import { GreenCheckBox } from '../../components/CheckBox'
import { GreenButton } from '../../components/Buttons/GreenButton'
import { ReactComponent as LogoImg } from '../../images/login/logo.svg'
import capitalizeFirstLetter from '../../utils/capitalizeFirstLetter'
import { AppStateContext, useAuth, useUserId, useUserName, useUserRole } from '../../components/Context'

const resolver = classValidatorResolver(CrmLoginRequestDto)

type Inputs = {
  username: string
  password: string
  remember: boolean
}

const CustomFormControl = withStyles((theme: Theme) => ({
  root: {
    '& label': {
      color: theme.palette.primary.light,
      opacity: '60%',
    },
    '& input:valid': {
      fontSize: 16,
      border: '1px solid rgba(255,255,255,0.6)',
      borderRadius: 4,
      padding: 10,
    },
    '& input:valid:hover + fieldset': {
      fontSize: 16,
      border: `1px solid ${theme.palette.primary.light}`,
    },
    '& input:valid:active + fieldset': {
      fontSize: 16,
      border: `1px solid ${theme.palette.secondary.main}`,
    },
    '& input:valid + fieldset': {
      fontSize: 16,
      opacity: 0.6,
      color: theme.palette.primary.light,
    },
    '& fieldset': {
      color: 'red',
    },
  },
}))(FormControl)

export const Login: FC = () => {
  const classes = useStyles()
  const formMethods = useForm<Inputs>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldFocusError: true,
    resolver,
  })

  const {
    handleSubmit,
    register,
    formState: { errors },
    getValues,
  } = formMethods

  const history = useHistory()
  const { setAuthAccessToken, setAccessTokenData, getAccessTokenDataFromAuthToken } = useAuth()
  const { getLastLocation } = useContext(AppStateContext)
  const [loading, setLoading] = useState<boolean>(false)
  const [lastLocation, setLastLocation] = useState<string>()

  const { saveUserName } = useUserName()
  const { saveUserRole } = useUserRole()
  const { saveUserId } = useUserId()

  useEffect(() => setLastLocation(getLastLocation()), [setLastLocation, getLastLocation])

  const onSubmit: SubmitHandler<Inputs> = useCallback(
    async (data: Inputs) => {
      let cleanupFunction = false
      setLoading(true)

      rxios.post('login', data).subscribe(
        // eslint-disable-next-line
        (next: any) => {
          const { accessToken } = next

          if (accessToken) {
            setAuthAccessToken(accessToken)
            const accessTokenData = getAccessTokenDataFromAuthToken(accessToken)

            if (accessTokenData) {
              setAccessTokenData(accessTokenData)
              saveUserName(`${accessTokenData?.firstName} ${accessTokenData?.lastName}`)
              saveUserRole(accessTokenData?.role)
              saveUserId(accessTokenData?.id)
              toast.success('Login is successful')
              if (!cleanupFunction) setLoading(false)
              history.push(lastLocation || routes.HOME)
            } else {
              toast.error('Can not login')
              setLoading(false)
            }
          } else {
            toast.error('Can not login')
            setLoading(false)
          }
        },
        // eslint-disable-next-line
        (error: any) => {
          toast.error(error.response.data.message)
          setLoading(false)
        },
        () => {
          // eslint-disable-next-line
          console.log('completed')
          if (!cleanupFunction) setLoading(false)
        },
      )
      cleanupFunction = true
    },
    [
      getAccessTokenDataFromAuthToken,
      history,
      lastLocation,
      saveUserId,
      saveUserName,
      saveUserRole,
      setAccessTokenData,
      setAuthAccessToken,
    ],
  )

  const errorMessage = useCallback(
    (inputName: string) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const error = errors[inputName]
      const message = error?.message.replace(inputName, '')
      return capitalizeFirstLetter(message)
    },
    [errors],
  )

  return (
    <FormProvider {...formMethods}>
      <main className={classes.main}>
        <Grid container className={classes.container}>
          <LogoImg style={{ width: 200, height: 58 }} />

          <form className={classes.form} noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
            <Grid container className={classes.formWrap}>
              <Grid item xs={12} className={classes.inputWrap}>
                <CustomFormControl className={classes.formControl}>
                  <InputLabel shrink htmlFor="username" className={classes.label}>
                    Email
                  </InputLabel>
                  <TextField
                    className={classes.input}
                    id="username"
                    variant="outlined"
                    fullWidth
                    {...register('username')}
                  />
                </CustomFormControl>
                {errors.username && (
                  <Typography variant="body1" gutterBottom className={classes.errorField}>
                    {errorMessage('username')}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} className={classes.inputWrap}>
                <CustomFormControl className={classes.formControl}>
                  <InputLabel shrink htmlFor="password" className={classes.label}>
                    Password
                  </InputLabel>
                  <TextField
                    className={classes.input}
                    id="password"
                    variant="outlined"
                    fullWidth
                    type="password"
                    {...register('password')}
                  />
                </CustomFormControl>
                {errors.password && (
                  <Typography variant="body1" gutterBottom className={classes.errorField}>
                    {getValues('password').length < 1 ? 'This field is required' : errorMessage('password')}
                  </Typography>
                )}
              </Grid>

              <Grid container className={classes.formBottom}>
                <GreenCheckBox checkBoxLabel={'Remember me'} checkBoxName={'remember'} styles={{ fontSize: 14 }} />

                <NavLink to="/reset-password" className={classes.link}>
                  <Typography className={classes.forgotPass}>Forgot password</Typography>
                </NavLink>
              </Grid>

              <GreenButton type="submit" width="100%" mt={18} mb={30} disabled={loading} text="Sign In" />
            </Grid>
          </form>
        </Grid>
      </main>
    </FormProvider>
  )
}
